import React, { useState, useEffect, useRef } from 'react';

interface LazySectionProps {
  children: React.ReactNode;
  placeholderHeight?: number | string;
  rootMargin?: string;
}

/**
 * LazySection defers DOM instantiation of below-the-fold sections until the user scrolls near.
 * Keeps initial DOM size small (<400 nodes) for optimal mobile FCP, LCP, and Speed Index.
 */
export const LazySection: React.FC<LazySectionProps> = ({
  children,
  placeholderHeight = '420px',
  rootMargin = '400px',
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver !== 'undefined' && containerRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShouldRender(true);
            observer.disconnect();
          }
        },
        { rootMargin }
      );
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    } else {
      const timer = setTimeout(() => setShouldRender(true), 250);
      return () => clearTimeout(timer);
    }
  }, [rootMargin]);

  return (
    <div ref={containerRef} className="cv-auto">
      {shouldRender ? (
        children
      ) : (
        <div
          style={{ minHeight: typeof placeholderHeight === 'number' ? `${placeholderHeight}px` : placeholderHeight }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
